import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Clock,
  Edit,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Ticket,
  TrendingUp,
  Globe,
  Lock,
  Star,
  Loader2,
  MoreHorizontal,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, formatDateTime, formatCurrency, formatNumber, getStatusColor, calculatePercentage } from '@utils';
import api from '@services/api';
import type { Event } from '@types';
import { ProgressBar } from '../../components/ui/ProgressBar';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get<{ data: Event }>(`/events/${id}`),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/events/${id}`),
    onSuccess: () => {
      toast.success('Event deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/events');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to delete event'),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => api.post(`/events/${id}/duplicate`),
    onSuccess: () => {
      toast.success('Event duplicated successfully');
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to duplicate event'),
  });

  const event = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gray-200 rounded-lg dark:bg-gray-700" />
          <div className="h-8 bg-gray-200 rounded w-48 dark:bg-gray-700" />
        </div>
        <div className="h-64 bg-gray-200 rounded-xl dark:bg-gray-700" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
          <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Event not found</h2>
        <Link to="/events" className="btn-primary mt-4 inline-flex">Back to Events</Link>
      </div>
    );
  }

  const soldPct = calculatePercentage(event.soldCount, event.capacity);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/events')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Back to Events"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{event.name}</h1>
              <span className={cn('badge', getStatusColor(event.status))}>{event.status}</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Created {formatDate(event.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/events/${id}/edit`} className="btn-primary">
            <Edit className="w-4 h-4" />
            Edit Event
          </Link>
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="btn-secondary p-2"
              aria-label="Event Actions"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {showActions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                  <button
                    onClick={() => { duplicateMutation.mutate(); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Duplicate
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(true); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero Image */}
      {event.featuredImage && (
        <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
          <img
            src={event.featuredImage}
            alt={event.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3">
            {event.isFeatured && (
              <span className="badge bg-accent-100 text-accent-800 border-accent-200">
                <Star className="w-3 h-3 mr-1" /> Featured
              </span>
            )}
            {event.isPrivate ? (
              <span className="badge bg-gray-100 text-gray-800 border-gray-200">
                <Lock className="w-3 h-3 mr-1" /> Private
              </span>
            ) : (
              <span className="badge bg-primary-100 text-primary-800 border-primary-200">
                <Globe className="w-3 h-3 mr-1" /> Public
              </span>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Capacity</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(event.capacity)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success-50 dark:bg-success-900/20 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sold</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(event.soldCount)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning-50 dark:bg-warning-900/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-warning-600 dark:text-warning-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Available</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(event.availableCount)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sell-through</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{soldPct}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details + Ticket Types */}
        <div className="lg:col-span-2 space-y-6">
          {/* Event Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Event Details</h3>
            {event.description && (
              <div className="prose dark:prose-invert max-w-none mb-6">
                <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{event.description}</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Start Date</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(event.startDate)}</p>
                </div>
              </div>
              {event.endDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">End Date</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(event.endDate)}</p>
                  </div>
                </div>
              )}
              {event.venue && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Venue</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{event.venue.name}</p>
                    <p className="text-xs text-gray-400">{event.venue.address}, {event.venue.city}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Type</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{event.type}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Types */}
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ticket Types</h3>
            </div>
            {event.ticketTypes && event.ticketTypes.length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Name</th>
                      <th className="table-header-cell">Price</th>
                      <th className="table-header-cell">Sold / Capacity</th>
                      <th className="table-header-cell">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {event.ticketTypes.map((tt) => {
                      const ttPct = calculatePercentage(tt.soldCount, tt.capacity);
                      const isSoldOut = tt.availableCount === 0;
                      return (
                        <tr key={tt.id} className="table-row">
                          <td className="table-cell">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{tt.name}</p>
                              {tt.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{tt.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="table-cell font-medium">
                            {tt.price === 0 ? 'Free' : formatCurrency(tt.price, tt.currency)}
                            {tt.compareAtPrice && tt.compareAtPrice > tt.price && (
                              <span className="text-xs text-gray-400 line-through ml-2">
                                {formatCurrency(tt.compareAtPrice, tt.currency)}
                              </span>
                            )}
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center gap-3">
                              <div className="w-20">
                                <ProgressBar
                                  percentage={ttPct}
                                  className={isSoldOut ? 'bg-danger-500' : ttPct > 80 ? 'bg-warning-500' : 'bg-primary-500'}
                                />
                              </div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {tt.soldCount}/{tt.capacity}
                              </span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <span className={cn('badge', isSoldOut ? 'bg-danger-100 text-danger-800 border-danger-200' : 'bg-success-100 text-success-800 border-success-200')}>
                              {isSoldOut ? 'Sold Out' : 'Available'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">
                <Ticket className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No ticket types configured</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Sales Progress */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sales Progress</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-500 dark:text-gray-400">Tickets Sold</span>
                  <span className="font-medium text-gray-900 dark:text-white">{soldPct}%</span>
                </div>
                <div className="w-full">
                  <ProgressBar
                    percentage={soldPct}
                    className={soldPct >= 90 ? 'bg-danger-500' : soldPct >= 70 ? 'bg-warning-500' : 'bg-primary-500'}
                    containerClassName="h-3"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(event.soldCount)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sold</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(event.availableCount)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
                </div>
              </div>
            </div>
          </div>

          {/* Price Range */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pricing</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">From</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {event.minTicketPrice === 0 ? 'Free' : formatCurrency(event.minTicketPrice || 0, event.currency)}
                </span>
              </div>
              {event.maxTicketPrice !== undefined && event.maxTicketPrice !== event.minTicketPrice && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">To</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(event.maxTicketPrice, event.currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Currency</span>
                <span className="font-medium text-gray-900 dark:text-white">{event.currency}</span>
              </div>
            </div>
          </div>

          {/* Event Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Info</h3>
            <div className="space-y-3 text-sm">
              {event.category && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Category</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{event.category}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Timezone</span>
                <span className="font-medium text-gray-900 dark:text-white">{event.timezone}</span>
              </div>
              {event.salesStartAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Sales Start</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatDate(event.salesStartAt)}</span>
                </div>
              )}
              {event.salesEndAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Sales End</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatDate(event.salesEndAt)}</span>
                </div>
              )}
              {event.tags && event.tags.length > 0 && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {event.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full dark:bg-gray-700 dark:text-gray-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Event</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Are you sure you want to delete <strong>{event.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn-primary bg-danger-600 hover:bg-danger-700"
              >
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
